import * as _ from 'lodash';
import {Region} from "../classes";

export default angular.module('cian.regionUtils', [])
  .service('RegionUtilsService', ['$window', 'RegionIds', ($window, RegionIds) => {
    const service = {};

    /**
     * Конвертирует данные от бэка во фронтовую модель
     * @memberOf RegionUtilsService
     * @param {object[]} regionsLikeData
     * @return {Region[]}
     */
    service.convertToRegions =
      regionsLikeData => regionsLikeData.map(r => new Region(r));

    // Сортируем по id, чтобы главный регион (Москва или Спб) был на 1 месте
    const initialRegions = _.sortBy(
      service.convertToRegions($window._currentRoots), 'id'
    );
    const topRegions = service.convertToRegions($window._regions);

    /**
     * Отдаёт список регионов России
     * @memberOf RegionUtilsService
     * @return {Region[]}
     */
    service.getTopRegions = () => topRegions;

    /**
     * Находит регион России с указанным id
     * @memberOf RegionUtilsService
     * @param {number} id
     * @return {Region}
     */
    service.getTopRegion = id => _.find(topRegions, { id });

    /**
     * Отдаёт список изначально выбранных регионов
     * @memberOf RegionUtilsService
     * @return {Region[]}
     */
    service.getInitialRegions = () => initialRegions;

    /**
     * Находит группу, соответствующую выбранным регионам
     * @memberOf RegionUtilsService
     * @param {Region[]} regions
     * @return {?RegionGroup}
     */
    service.findGroup = regions => {
      const regionIds = _.map(regions, 'id');
      return _.find(RegionIds.GROUPS,
        // Проверяем, что нет лишних регионов ни в одном из массивом
        group => _.xor(group.regionIds, regionIds).length === 0
      );
    };

    /**
     * Находит группу, в которую входит регион
     * @memberOf RegionUtilsService
     * @param {Region} region
     * @return {?RegionGroup}
     */
    service.findContainingGroup = region => _.find(RegionIds.GROUPS,
      group => _.includes(group.regionIds, region.id)
    );

    service.isInSelected = function (selectedRegions, regionId) {
      return !!selectedRegions.some(function (region) {
        return region.id === regionId;
      });
    };

    service.selectedRegionHasHighway = function (selectedRegions) {
      // Даже если выбрана группа, на первом месте основной регион группы
      return selectedRegions[0] && selectedRegions[0].hasHighway;
    };

    service.selectedRegionHasDistricts = function (selectedRegions) {
      return selectedRegions[0] && selectedRegions[0].hasDistricts;
    };

    service.selectedRegionHasMetro = function (selectedRegions) {
      return selectedRegions[0] && selectedRegions[0].hasMetro;
    };

    service.isMoscow = function (selectedRegions) {
      return service.isInSelected(selectedRegions, RegionIds.REGIONS.MOSCOW);
    };

    /**
     * @memberOf RegionUtilsService
     * @param {Region[]} selectedRegions
     * @return {boolean}
     */
    service.isMO = function (selectedRegions) {
      return service.isInSelected(selectedRegions, RegionIds.REGIONS.MO);
    };

    /**
     * @memberOf RegionUtilsService
     * @param {Region[]} selectedRegions
     * @return {boolean}
     */
    service.isMoscowOrMO = function (selectedRegions) {
      return service.isMoscow(selectedRegions) || service.isMO(selectedRegions);
    };

    /**
     * @memberOf RegionUtilsService
     * @param {Region[]} selectedRegions
     * @return {boolean}
     */
    service.isSpbOrLO = selectedRegions => (
      service.isInSelected(selectedRegions, RegionIds.REGIONS.SPB) ||
      service.isInSelected(selectedRegions, RegionIds.REGIONS.LO)
    );

    /**
     * @memberOf RegionUtilsService
     * @param {Region[]} selectedRegions
     * @return {Region}
     */
    service.findRegionWithHighway = function (selectedRegions) {
      var regionsWithHighway = selectedRegions.filter(function (region) {
        return region.hasHighway;
      });
      return regionsWithHighway.length && regionsWithHighway[0];
    };

    /**
     * @memberOf RegionUtilsService
     * @param {Region[]} regionsA
     * @param {Region[]} regionsB
     * @return {boolean}
     */
    service.areRegionsEqual =
      (regionsA, regionsB) => _.xorBy(regionsA, regionsB, 'id').length === 0;

    /**
     * @memberOf RegionUtilsService
     * Проверяет, нужно ли сохранять гео-объекты при переключении

     * @example
     * // Переключение между разными регионами
     * Москва -> Белгород = false

     * // Переключение внутри группы
     * Москва -> Москва+МО = true
     * Москва+МО -> Москва = false
     * Москва -> МО = false

     * // Переключение между городом и областью:
     * Самара -> Самарская обл. = true
     * Самарская обл. -> Самара = false

     * @param {Region[]} regionsFrom
     * @param {Region[]} regionsTo
     * @return {boolean}
     */
    service.areRegionsCompatible = (regionsFrom, regionsTo) => {
      const switchingFromCityToRegion = (
        regionsFrom.length === 1 &&
        regionsFrom[0].isCity() &&
        service.getCommonTopRegion(regionsFrom) === service.getCommonTopRegion(regionsTo)
      );
      const switchingToGroup = _.differenceBy(regionsFrom, regionsTo, 'id').length === 0;
      return switchingFromCityToRegion || switchingToGroup;
    };

    /**
     * Находит общего родителя для группы регионов
     * @memberOf RegionUtilsService
     * @example
     * Самара, Тольятти -> Самарская обл.
     * Самара, Химки -> undefined
     * Самарская обл. -> Самарская обл.
     * Самара, Самарская обл. -> Самарская обл.
     * @param {Region[]} regions
     * @return {?Region}
     */
    service.getCommonTopRegion = regions => {
      const parents = _.groupBy(regions, region => region.parentId || region.id);
      if (_.size(parents) === 1) {
        const commonParentId = Number(_.keys(parents)[0]);
        if (commonParentId) {
          return service.getTopRegion(commonParentId);
        }
      }
    };

    /**
     * @memberOf RegionUtilsService
     * @param {Region[]} regions
     * @return {boolean}
     */
    service.areNewBuildingsAvailable = (regions) => {
      return _.difference(_.map(regions, 'id'), RegionIds.newBuildingRegions).length === 0 ||
        // Если самого региона нет в списке, то проверяем его родителя
        _.includes(RegionIds.newBuildingRegions, service.getCommonTopRegion(regions).id);
    };

    service.isMoscowMOSpbLOOrAnyChildCityInTheseRegions = (regions) => {
      const MOSCOW_MO_SPB_LO_REGION_IDS = [
        RegionIds.REGIONS.MOSCOW,
        RegionIds.REGIONS.MO,
        RegionIds.REGIONS.SPB,
        RegionIds.REGIONS.LO
      ];

      return _.intersection(regions.reduce((memo, region) => {
          return memo.concat(region.id, region.parentId)
        }, []), MOSCOW_MO_SPB_LO_REGION_IDS).length > 0;
    };

    return service;
  }])
  .name;



// WEBPACK FOOTER //
// ./src/serp/services/region-utils.service.js